(ns common.entity
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.schema.calculated-field :as calculate-field]
   [common.schema.entities.pre-exit-check :as pre-exit-check]
   [common.schema.entities.pre-investment-check :as pre-investment-check]
   [common.schema.entities.role :as role]
   [common.schema.entities.task-type :as task-type]
   [common.schema.entity-types :as entity-types]
   [common.schema.option-filters :as option-filters]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [schema.core :as s]))

(s/defschema EID
  "Entity ID is a generic positive integer used to reference another entity."
  (s/conditional
   string?
   s/Str
   integer?
   s/Int
   (fn [e]
     (boolean (:db/id e)))
   {:db/id s/Int
    s/Any  s/Any}))

(defn ref-equals?
  [a b]
  (= (or (:db/id a) a)
     (or (:db/id b) b)))

(defn default-icon
  [entity-or-primitive]
  (let [entity-type (:entity/type entity-or-primitive)
        configured-icon (-> entity-types/entities
                            (get entity-type)
                            :entity/icon)]
    (or configured-icon
        (case entity-type
          :entity.type/system-activity (case (:system-activity/type entity-or-primitive)
                                         :system-activity.type/pass :fa-ban
                                         :system-activity.type/put-on-hold :fa-pause
                                         :system-activity.type/reactivate :fa-lock-open
                                         :system-activity.type/stage-change :fa-arrow-alt-right
                                         :fa-exclamation-circle)
          :fa-project-diagram))))

(defn entity-type-icon
  [entity-type]
  (default-icon {:entity/type entity-type}))

(defn get-first-name
  [entity]
  (or (:person/first-name entity)
      (:account/first-name entity)))

(defn get-last-name
  [entity]
  (or (:person/last-name entity)
      (:account/last-name entity)))

(defn get-initials
  [entity]
  (let [first-name (get-first-name entity)
        last-name (get-last-name entity)
        [f l] (map first [first-name last-name])]
    (->> [f l]
         (string/join " ")
         (string/trim))))

(defn contact-name
  [entity]
  (str (string/trim
        (string/join
         " "
         [(get-first-name entity)
          (get-last-name entity)]))))

(declare display-string)

(defn display-string
  ([entity-or-primitive]
   (display-string entity-or-primitive :short))
  ([entity-or-primitive mode]
   (display-string entity-or-primitive mode identity))
  ([entity-or-primitive mode entity-lookup]
   (let [id (:db/id entity-or-primitive)
         e-type (:entity/type entity-or-primitive)]
     (if (nil? id)
       entity-or-primitive
       (case e-type
         :entity.type/workspace (:workspace/name entity-or-primitive)
         :entity.type/check-list (:check-list/label entity-or-primitive)
         :entity.type/account
         (let [name-str (->> [(:account/first-name entity-or-primitive)
                              (:account/last-name entity-or-primitive)
                              (when (= mode :long)
                                (:account/email entity-or-primitive))]
                             (remove cu/blank-value?)
                             (string/join " "))]
           (if (empty? name-str)
             (string/trim (str (:account/email entity-or-primitive)))
             name-str))
         :entity.type/attachment (:attachment/filename entity-or-primitive)
         :entity.type/contact-list (:contact-list/title entity-or-primitive)
         :entity.type/company (:asset/name entity-or-primitive)
         :entity.type/real-estate (:asset/name entity-or-primitive)
         :entity.type/fof-asset (:asset/name entity-or-primitive)
         :entity.type/other-asset (:asset/name entity-or-primitive)
         :entity.type/email (:email/subject entity-or-primitive)
         :entity.type/oppo (:oppo/name entity-or-primitive)
         :entity.type/person (option-filters/person-display-string entity-or-primitive mode)
         :entity.type/role (->> (get role/roles (:role/role entity-or-primitive))
                                :translations
                                :en)
         :entity.type/zap-vault (:zap-vault/name entity-or-primitive)
         :entity.type/stage (:stage/name entity-or-primitive)
         :entity.type/opportunity-stage (:opportunity-stage/name entity-or-primitive)
         :entity.type/sales-opportunity-stage (:sales-opportunity-stage/name entity-or-primitive)
         :entity.type/exit-opportunity-stage (:exit-opportunity-stage/name entity-or-primitive)
         :entity.type/task (task-type/get-label (:task/type entity-or-primitive))
         :entity.type/tenant (:tenant/name entity-or-primitive)
         :entity.type/named-value (:named-value/value entity-or-primitive)
         :entity.type/named-view (:named-view/name entity-or-primitive)
         :entity.type/view (:view/name entity-or-primitive)
         :entity.type/token (:token/type entity-or-primitive)
         :entity.type/fund-commitment (calculate-field/calculate
                                       {:calc-fn          (->> :fund-commitment/name
                                                               (registry/get-field-schema nil)
                                                               :field/calculated-field)
                                        :entity-data      entity-or-primitive
                                        :display-string   display-string
                                        :entity-lookup    entity-lookup
                                        :get-field-schema (partial registry/get-field-schema nil)})
         :entity.type/fundraising-opportunity (:fundraising-opportunity/opportunity-name
                                               entity-or-primitive)
         :entity.type/sales-opportunity (:sales-opportunity/name entity-or-primitive)
         :entity.type/sale-agreement (:sale-agreement/name entity-or-primitive)
         :entity.type/exit-opportunity (:exit-opportunity/name entity-or-primitive)
         :entity.type/graph-company (:graph-entity/label entity-or-primitive)
         :entity.type/graph-config (:graph-config/name entity-or-primitive)
         :entity.type/graph-person (:graph-entity/label entity-or-primitive)
         :entity.type/graph-fof-asset (:graph-entity/label entity-or-primitive)
         :entity.type/graph-fund (:graph-entity/label entity-or-primitive)
         :entity.type/holding (:holding/name entity-or-primitive)
         :entity.type/kpi-category (:kpi-category/name entity-or-primitive)
         :entity.type/fullcontact-company-common (:fullcontact-company-common/name
                                                  entity-or-primitive)
         :entity.type/fullcontact-person-common (:fullcontact-person-common/full-name
                                                 entity-or-primitive)
         :entity.type/team (:team/name entity-or-primitive)
         :entity.type/transaction-process (or (:transaction-process/short-name entity-or-primitive)
                                              (:transaction-process/name entity-or-primitive))
         :entity.type/fund (:fund/name entity-or-primitive)
         :entity.type/investor (:investor/name entity-or-primitive)
         :entity.type/pipeline (let [pipeline-name (:pipeline/name entity-or-primitive)
                                     archived? (:pipeline/inactive? entity-or-primitive)
                                     display-name (if archived?
                                                    (loc/tr :inactive-pipeline-name [pipeline-name])
                                                    pipeline-name)]
                                 display-name)
         :entity.type/kyc-profile (let [target-id (:kyc-profile/target entity-or-primitive)
                                        target (entity-lookup target-id)
                                        target-name (when (map? target)
                                                      (display-string target))]
                                    (if target-name
                                      (str target-name "'s " (loc/tr :entity.type/kyc-profile))
                                      (loc/tr :entity.type/kyc-profile)))
         :entity.type/tenant-kyc-profile (let [target-id (:tenant-kyc-profile/target entity-or-primitive)
                                               target (entity-lookup target-id)
                                               target-name (when (map? target)
                                                             (display-string target))]
                                           (if target-name
                                             (str target-name "'s " (loc/tr :entity.type/tenant-kyc-profile))
                                             (loc/tr :entity.type/tenant-kyc-profile)))
         :entity.type/contract-kyc-profile (let [target-id (:contract-kyc-profile/target entity-or-primitive)
                                                 target (entity-lookup target-id)
                                                 target-name (when (and target
                                                                        (not (cu/blank-value? (:asset/name target))))
                                                               (display-string target))]
                                             (if target-name
                                               (str target-name "'s " (loc/tr :entity.type/contract-kyc-profile))
                                               (loc/tr :entity.type/contract-kyc-profile)))
         :entity.type/kyc-risk-category (:kyc-risk-category/name entity-or-primitive)
         :entity.type/entity-association (if entity-lookup
                                           (str (->> entity-or-primitive
                                                     :entity-association/target-entity
                                                     ((fn [entity-or-id]
                                                        (if (:db/id entity-or-id)
                                                          entity-or-id
                                                          (entity-lookup entity-or-id))))
                                                     (display-string))
                                                " - "
                                                (->> entity-or-primitive
                                                     :entity-association/source-entity
                                                     ((fn [entity-or-id]
                                                        (if (:db/id entity-or-id)
                                                          entity-or-id
                                                          (entity-lookup entity-or-id))))
                                                     (display-string)))
                                           (str entity-or-primitive))
         :entity.type/risk-holding (loc/tr :risk-display-string [(:risk/title entity-or-primitive)])
         :entity.type/liability (loc/tr :liability-display-string
                                    [(:liability/description entity-or-primitive)])
         :entity.type/calculated-indirect-holding
         (when entity-lookup
           (let [fund-id (:indirect-holding/fund entity-or-primitive)
                 fund (entity-lookup fund-id)
                 target-id (:indirect-holding/holding-target entity-or-primitive)
                 target-asset (entity-lookup target-id)]
             (str (display-string fund mode entity-lookup)
                  " / "
                  (display-string target-asset mode entity-lookup))))
         :entity.type/survey-type (:survey-type/label entity-or-primitive)
         :entity.type/survey-template (:survey-template/label entity-or-primitive)
         :entity.type/survey-block (:survey-block/label entity-or-primitive)
         :entity.type/rotation-period (:rotation-period/rotation-period-label
                                       entity-or-primitive)
         :entity.type/reminder-rule (:reminder-rule/rule-label entity-or-primitive)
         :entity.type/survey-cycle (:survey-cycle/cycle-label entity-or-primitive)
         :entity.type/survey-batch (:survey-batch/batch-label entity-or-primitive)
         ;; An override has no name of its own - it is identified by the
         ;; rotation period it overrides, the way a KYC profile is identified by
         ;; its target above.
         :entity.type/rotation-period-override
         (let [rotation-period-id (:rotation-period-override/rotation-period entity-or-primitive)
               rotation-period (entity-lookup rotation-period-id)
               rotation-period-name (when (map? rotation-period)
                                      (display-string rotation-period))]
           (if rotation-period-name
             (str (loc/tr :entity.type/rotation-period-override)
                  " - "
                  rotation-period-name)
             (loc/tr :entity.type/rotation-period-override)))
         :entity.type/insider-group (:insider-group/name entity-or-primitive)
         :entity.type/metabase-dashboard (:metabase-dashboard/label entity-or-primitive)
         :entity.type/lp-news-category (or (:lp-news-category/display-name entity-or-primitive)
                                           (:lp-news-category/source-name entity-or-primitive))
         :entity.type/lp-news-type (or (:lp-news-type/display-name entity-or-primitive)
                                       (:lp-news-type/source-name entity-or-primitive))
         :entity.type/pre-investment-check (let [status (get entity-or-primitive :pre-investment-check/status)]
                                             (-> (group-by :key pre-investment-check/status-options)
                                                 (get status)
                                                 first
                                                 :label
                                                 (or (loc/tr :unchecked))))
         :entity.type/pre-exit-check (let [status (get entity-or-primitive :pre-exit-check/status)]
                                       (-> (group-by :key pre-exit-check/status-options)
                                           (get status)
                                           first
                                           :label
                                           (or (loc/tr :unchecked))))
         :entity.type/investment-reservation-type
         (:investment-reservation-type/title entity-or-primitive)
         :entity.type/survey-profile (let [target-id (:survey-profile/target entity-or-primitive)
                                           target (entity-lookup target-id)
                                           target-name (when (map? target)
                                                         (display-string target))]
                                       (if target-name
                                         (str target-name "'s " (loc/tr :entity.type/survey-profile))
                                         (loc/tr :entity.type/survey-profile)))
         :entity.type/project-counterparty-diligence
         (:project-counterparty-diligence/project-name entity-or-primitive)
         :entity.type/company-counterparty-diligence
         (:company-counterparty-diligence/company-name entity-or-primitive)
         :entity.type/person-counterparty-diligence
         (:person-counterparty-diligence/person-name entity-or-primitive)
         :entity.type/aifm-decision (loc/tr e-type)
         :entity.type/aifm-exit-decision (loc/tr e-type)
         :entity.type/ic-decision (loc/tr e-type)
         :entity.type/ic-exit-decision (loc/tr e-type)
         :entity.type/advisory-board-decision (loc/tr e-type)
         :entity.type/pm-approval-decision (loc/tr e-type)
         :entity.type/decision-condition   (:decision-condition/title entity-or-primitive)
         :entity.type/attestation-condition (:attestation-condition/title entity-or-primitive)
         :entity.type/decision-participant (let [person-id (:decision-participant/person entity-or-primitive)
                                                 person (entity-lookup person-id)]
                                             (display-string person))
         ;; TODO: Hack(??) we pass {:db/id 1 :value 1 :label "xyz"} from open search results as temp optimistic value
         (if-let [label (:label entity-or-primitive)]
           label
           (str e-type)))))))

(defn resolve-id
  [entity-or-id]
  (or (:db/id entity-or-id)
      (when (number? entity-or-id)
        entity-or-id)))

(defn resolve-reference
  [entity attribute entity-lookup]
  (->> (get entity attribute)
       (resolve-id)
       (entity-lookup)))

(defn unique-name-or-id
  [entity-or-primitive]
  (let [id (:db/id entity-or-primitive)
        e-type (:entity/type entity-or-primitive)]
    (case e-type
      :entity.type/account (:account/email entity-or-primitive)
      :entity.type/company (:asset/name entity-or-primitive)
      :entity.type/oppo (:oppo/name entity-or-primitive)
      :entity.type/person (or (:person/universal-email-address entity-or-primitive)
                              (:db/id entity-or-primitive))
      :entity.type/tenant (:tenant/name entity-or-primitive)
      (if (nil? id)
        entity-or-primitive
        e-type))))

(defn entity->label-value
  [e]
  {:value (:db/id e)
   :label (display-string e)})

(defn entities->label-values
  [entity-coll]
  (map entity->label-value entity-coll))

(defn entity?
  [e]
  (or (:entity/type e)
      (:db/id e)))

(defn resolve-entity-name
  [entity entity-lookup reverse-lookup]
  (let [entity (cond (number? entity)
                     (entity-lookup entity)
                     :else
                     entity)
        name-attribute (registry/resolve-name-attribute (:entity/type entity))
        name-schema (registry/get-field-schema nil name-attribute)
        calc-function (:field/calculated-field name-schema)
        use-display-string? (or (not name-schema)
                                ;; handle 'archived' pipelines. can't use calc-fn because
                                ;;  pipeline-name is editable - using a special case for now:
                                (= name-attribute :pipeline/name))
        name-value (cond
                     use-display-string?
                     (display-string entity :short entity-lookup)
                     (fn? calc-function)
                     (calculate-field/calculate
                      {:calc-fn          calc-function
                       :entity-data      entity
                       :display-string   display-string
                       :reverse-lookup   reverse-lookup
                       :entity-lookup    entity-lookup
                       :get-field-schema (partial registry/get-field-schema nil)})
                     :else
                     (get entity name-attribute))]
    name-value))

;; display-string passed explicitly for performance (resolve-entity-name should be used for that):
(defn search-strings
  [entity display-string]
  (let [et (:entity/type entity)]
    ;; 'or' instead of 'contains?' for performance:
    (if-not (or (= et :entity.type/person)
                (= et :entity.type/account))
      [display-string]
      (concat [display-string]
              (when-let [first-name (:person/first-name entity)]
                (when-not (cu/blank-value? first-name)
                  [first-name]))
              (when-let [last-name (:person/last-name entity)]
                (when-not (cu/blank-value? last-name)
                  [last-name]))
              (let [email (case et
                            :entity.type/person (:person/universal-email-address entity)
                            :entity.type/account (:account/email entity)
                            nil)
                    trimmed (some-> email
                                    (str)
                                    (string/replace #"[.@]" "")
                                    (string/trim))]
                (when-not (cu/blank-value? email)
                  [trimmed]))))))

(defn resolve-composite-attribute-value
  [composite-attributes entity entity-lookup]
  (string/join " - "
               (->> composite-attributes
                    (map (fn [a]
                           (let [fs (registry/get-field-schema nil a)
                                 data-type (:field/data-type fs)
                                 value (get entity a)]
                             (case data-type
                               :data.type/ref-single
                               (display-string (entity-lookup value))
                               (str value)))))
                    (remove nil?))))

(defn debug-string
  [entity]
  (str "["
       (some-> entity
               :entity/type
               name)
       ":"
       (:db/id entity)
       "-"
       (display-string entity :long)
       "]"))

(defn resolve-entity-type-name
  [entity]
  (case (:entity/type entity)
    :entity.type/oppo (loc/tr :entity-type-oppo)
    :entity.type/fundraising-opportunity (loc/tr :entity-type-fundraising-opportunity)
    :entity.type/company (loc/tr :entity-type-company)
    :entity.type/person (loc/tr :entity-type-person)
    :entity.type/investor (loc/tr :entity-type-investor)
    :entity.type/holding (loc/tr :entity-type-holding)
    :entity.type/other-asset (loc/tr :entity-type-other-asset)
    :entity.type/real-estate (loc/tr :entity-type-real-estate)
    :entity.type/sales-opportunity (loc/tr :entity-type-sales-opportunity)
    :entity.type/sale-agreement (loc/tr :entity-type-sale-agreement)
    :entity.type/exit-opportunity (loc/tr :entity-type-exit-opportunity)
    :entity.type/fund-commitment (loc/tr :entity-type-fund-commitment)
    :entity.type/fund (loc/tr :entity-type-fund)
    :entity.type/kyc-profile (loc/tr :entity-type-kyc)
    :entity.type/fof-asset (loc/tr :entity-type-fof-asset)
    (loc/tr (:entity/type entity))))
